/**
 * All the columns that will be displayed in the table and the columns that will be mapped to the data.
 */

import moment from "moment";
import { cleanupPhone } from "../utils";

export interface ColumnType {
  key: string;
  label: string;
  isEmail: boolean;
  isPhone: boolean;
  possibleNames: string[];
  isMandatory: boolean;
  style?: {
    weight?: number;
  };
  parser?: (string) => string;
}

export const COLUMNS: ColumnType[] = [
  {
    key: "policyNumber",
    label: "Policy number",
    isEmail: false,
    isPhone: false,
    possibleNames: ["id", "policy number"],
    isMandatory: false,
    style: { weight: 0.8 },
  },
  {
    key: "insuredFirstName",
    label: "Insured First Name",
    isEmail: false,
    isPhone: false,
    possibleNames: ["insured first name", "first name", "firstname"],
    isMandatory: false,
    style: { weight: 0.8 },
  },
  {
    key: "insuredLastName",
    label: "Insured Last Name",
    isEmail: false,
    isPhone: false,
    possibleNames: ["insured last name", "last name", "lastname"],
    isMandatory: false,
    style: { weight: 0.8 },
  },
  {
    key: "insuredEmail",
    label: "Insured Email",
    isEmail: true,
    isPhone: false,
    isMandatory: false,
    possibleNames: ["insured email address"],
    style: { weight: 2 },
  },
  {
    key: "insuredPhone",
    label: "Insured Phone",
    isEmail: false,
    isPhone: true,
    isMandatory: false,
    possibleNames: ["insured phone number"],
    style: { weight: 0.8 },
    parser: cleanupPhone,
  },
  {
    key: "agentFirstName",
    label: "Agent First Name",
    isEmail: false,
    isPhone: false,
    possibleNames: ["agent first name"],
    isMandatory: false,
    style: { weight: 0.8 },
  },
  {
    key: "agentLastName",
    label: "Agent Last Name",
    isEmail: false,
    isPhone: false,
    possibleNames: ["agent last name"],
    isMandatory: false,
    style: { weight: 0.8 },
  },
  {
    key: "agentEmail",
    label: "Agent Email",
    isEmail: true,
    isPhone: false,
    possibleNames: ["agent email address"],
    isMandatory: false,
    style: { weight: 2 },
  },
  {
    key: "agentPhone",
    label: "Agent Phone",
    isEmail: false,
    isPhone: true,
    isMandatory: false,
    possibleNames: ["agent phone number", "agent phone"],
    style: { weight: 0.8 },
    parser: cleanupPhone,
  },
  {
    key: "agentLicense",
    label: "Agent License",
    isEmail: false,
    isPhone: false,
    isMandatory: false,
    possibleNames: ["agent license"],
    style: { weight: 0.8 },
  },
  {
    key: "effectiveDate",
    label: "Effective Date",
    isEmail: false,
    isPhone: false,
    isMandatory: false,
    possibleNames: ["increase offer effective date"],
    style: { weight: 0.8 },
    parser: excelDate => {
      const isWeirdDateStringAndNotAExcelDate = isNaN(excelDate);
      if (isWeirdDateStringAndNotAExcelDate) {
        const date = moment.tz(excelDate, "MM/DD/YYYY", "America/Los_Angeles");
        return date.format("L");
      } else {
        // ref: https://stackoverflow.com/questions/57145840/converting-excel-date-to-moment-date-gives-a-wrong-year-output
        const unixTimestamp = (parseFloat(excelDate) - 25569.0) * 86400;
        const date = moment.tz(unixTimestamp as any, "X", "UTC");
        return date.format("L");
      }
    },
  },
  {
    key: "monthlyBenefit",
    label: "Monthly Benefit",
    isEmail: false,
    isPhone: false,
    isMandatory: false,
    possibleNames: [],
    style: { weight: 0.8 },
  },
];

export const COLUMNS_WITH_CARRIER = [
  ...COLUMNS,
  {
    key: "carrierName",
    label: "Carrier Name",
    isEmail: false,
    isPhone: false,
    possibleNames: [],
    isMandatory: false,
    style: {},
  },
];

export const CARRIER_OPTIONS = [
  {
    key: "Assurity",
    value: 2,
  },
  {
    key: "Mass Mutual",
    value: 37,
  },
  {
    key: "Illinois Mutual",
    value: 54,
  },
  {
    key: "Lloyd's of London",
    value: 81,
  },
  {
    key: "Mutual of Omaha",
    value: 88,
  },
  {
    key: "Principal",
    value: 276,
  },
  {
    key: "The Standard",
    value: 288,
  },
  {
    key: "Ameritas",
    value: 397,
  },
];

const img = `data:image/png;base64,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
`;

const commonQueryParams = `data={{ allData }}&campaignStepId={{ campaignStepId }}&contactIndex={{ contactIndex }}"><img width="177" height="43" src="${img}" alt="Request a quote" /></a>`;

export const CTA_LINK_OPTIONS = [
  {
    label: "The Standard landing page",
    value: `<a href="https://www.diservices.com/lp/the-standard-increase-option?${commonQueryParams}`,
  },
  {
    label: "Ameritas landing page",
    value: `<a href="https://www.diservices.com/lp/ameritas-increase-option?${commonQueryParams}`,
  },
  {
    label: "Principal landing page",
    value: `<a href="https://www.diservices.com/lp/principal-increase-option?${commonQueryParams}`,
  },
  {
    label: "Mass Mutual landing page",
    value: `<a href="https://www.diservices.com/lp/mass-mutual-increase-option?${commonQueryParams}`,
  },
  {
    label: "GSI Renewal landing page",
    value: `<a href="https://www.diservices.com/lp/gsirenewal?${commonQueryParams}`,
  },
  {
    label: "Peterson landing page",
    value: `<a href="https://www.diservices.com/lp/petersen-international-underwriters-increase-option?${commonQueryParams}`,
  },
  {
    label: "IDU landing page",
    value: `<a href="https://www.diservices.com/lp/idu-increase-option?${commonQueryParams}`,
  },
];
